/* =============================================================================
   TSE-ADAPTER — Technische Sicherheits-Einrichtung (Phase 10a)
   =============================================================================
   Zentrale Schnittstelle für die KassenSichV-konforme Signatur von
   bezahlten Bestellungen. Drei Modi:

     - 'disabled' : keine Signatur. Für lokale Dev-Sessions ohne TSE.
     - 'mock'     : deterministische Pseudo-Signatur (SHA-256 über
                    order-Felder). Vollständiger Datenfluss inkl.
                    Audit-Log, aber OHNE rechtliche Konformität.
     - 'fiskaly'  : echte TSE-Signatur über fiskaly KassenSichV Cloud.
                    Edge Function `tse-sign` ruft die REST-API, schreibt
                    Werte in orders + tse_audit_log.

   FLOW im Cockpit (orders.js):
     1. Operator klickt «Bar» oder «Karte»
     2. orderMarkPaid → App.storage.orders.markPaid → mark_order_paid-RPC
        (atomare Rechnungsnummer + payment_status='paid')
     3. → signAndRecord(order) hier
        - mock : sha256(payload) lokal + record_tse_signature-RPC
        - fiskaly: tse-sign Edge Function → fiskaly-API → record_tse_signature
        - disabled: no-op
     4. Bon rendert tse_qr_payload als QR-Code
     5. tse_audit_log hält jede Aktion für die GoBD-Aufbewahrung fest

   Architektur-Vorgabe: pure Schnittstelle, der Caller (orders.js) ist
   unabhängig vom Modus. Mode-Wechsel im Settings-Tab über `set_tse_mode`-
   RPC; Adapter liest die Config beim nächsten Aufruf neu (10 s TTL-Cache,
   `clearTseConfigCache()` erzwingt Refresh).

   Diese Datei NICHT in app.js zurückkippen — sie ist Teil des core-Pakets
   neben reservation-engine.js, capacity.js, etc.
   ========================================================================== */

import { safeInvoke } from './edge-functions.js';
import {
  grossPerRateFromOrder,
  assertVatSplitMatchesTotal,
  buildProcessData,
  resolveItems
} from './tse-amounts.js';

const CONFIG_TTL_MS = 10_000;
let _cachedConfig = null;
let _cachedAt = 0;

/** Liefert den Supabase-Client. Wirft, wenn nicht initialisiert. */
function getSupa() {
  const supa = window.App && window.App.supabase;
  if (!supa) throw new Error('Supabase-Client nicht initialisiert.');
  return supa;
}

/**
 * Liest die TSE-Konfiguration aus internal_config über get_tse_config-RPC.
 * Liefert: { tse_mode, tse_fiskaly_tss_id, tse_fiskaly_client_id,
 *            tse_fiskaly_environment, last_audit_at, audit_count }
 * Cache: 10 s TTL.
 */
export async function getTseConfig({ force = false } = {}) {
  const now = Date.now();
  if (!force && _cachedConfig && (now - _cachedAt) < CONFIG_TTL_MS) {
    return _cachedConfig;
  }
  const supa = getSupa();
  const { data, error } = await supa.rpc('get_tse_config');
  if (error) {
    console.error('[tse] get_tse_config:', error);
    throw error;
  }
  _cachedConfig = data || { tse_mode: 'mock' };
  _cachedAt = now;
  return _cachedConfig;
}

/** Cache leeren — z.B. nach set_tse_mode oder set_tse_provisioning. */
export function clearTseConfigCache() {
  _cachedConfig = null;
  _cachedAt = 0;
}

/** Modus-Wechsel über RPC (nur Admin). */
export async function setTseMode(mode) {
  const supa = getSupa();
  const { data, error } = await supa.rpc('set_tse_mode', { p_mode: mode });
  if (error) throw error;
  clearTseConfigCache();
  return data;
}

/* --------------------------- Hash-Helfer (Mock) -------------------------- */

/** SHA-256 via Web Crypto API. Liefert Hex-String. */
async function sha256Hex(input) {
  const enc = new TextEncoder().encode(input);
  const buf = await window.crypto.subtle.digest('SHA-256', enc);
  return Array.from(new Uint8Array(buf))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

/** Hex → Base64 (für Signaturwert im BSI-QR-Code). */
function hexToBase64(hex) {
  if (typeof hex !== 'string' || (hex.length % 2) !== 0) return '';
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < bytes.length; i++) bytes[i] = parseInt(hex.substr(i * 2, 2), 16);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return window.btoa(bin);
}

/* --------------------------- Beträge je Steuersatz (S241 · HF-10) --------- */
/**
 * Ermittelt die Pro-Satz-Brutto-Beträge für einen Beleg und prüft die
 * KassenSichV-Invariante, BEVOR signiert wird.
 *
 * Bis S241 kannte die Edge-Function nur `total_gross_cents` + die beiden
 * Steuer-Cents und rechnete die Pro-Satz-Beträge daraus zurück
 * (`round(steuer/satz) + steuer`). Ergebnis auf JEDEM bisher signierten Bon:
 * 3–7 ct Abweichung je Satz, Σ Sätze ≠ Zahlbetrag (4,07 + 5,96 = 10,03 bei
 * einem 10,00-€-Beleg). Der Client kennt die echten Beträge aus den
 * Positionen — seit diesem Cut schickt er sie mit.
 *
 * @param {object} order  Order/Beleg (bereits „Pseudo-Order"-normalisiert)
 * @param {{items?: Array}} [opts]
 * @returns {{gross7Cents:number, gross19Cents:number, gross0Cents:number, source:string}}
 */
function resolveVatSplit(order, opts) {
  const split = grossPerRateFromOrder(order, opts);
  assertVatSplitMatchesTotal(split, split.totalGrossCents, order && order.id);
  return split;
}

/**
 * Positionen für die Betragsaufteilung besorgen.
 *
 * Reihenfolge: explizit übergeben → `order_items` aus der DB → was am
 * Order-Objekt hängt (Snapshot).
 *
 * **Warum die DB vor dem Snapshot:** `order_items` ist die autoritative
 * Aufzeichnung, `preorder_snapshot` nur eine Anzeige-Kopie — ihr fehlen unter
 * anderem die `customizations` und damit der `_dealMeta`-Marker, den die
 * Rabatt-Rekonstruktion braucht. Im Cockpit ist der Nachzug ohnehin Pflicht:
 * `markPaid` liefert nur den Order-Header (Memory
 * `gg-storage-orders-list-no-items`), die Positionen holt der Bon-Renderer
 * erst NACH der Signatur — ohne diesen Schritt liefe jede Theken-Bestellung
 * in den ungenauen Steuer-Fallback.
 *
 * Explizit übergebene Positionen gewinnen immer: beim Teil-Storno und beim
 * Teilbeleg beschreibt nur der Aufrufer, welche Zeilen zu DIESEM Beleg
 * gehören (und die Pseudo-Order hat gar keine eigene `order_items`-Zeile).
 */
async function resolveItemsForSigning(order, opts) {
  if (opts && Array.isArray(opts.items) && opts.items.length) return opts.items;

  const store = window.App && window.App.storage && window.App.storage.orders;
  const id = order && order.id;
  if (store && typeof store.listItems === 'function' && id) {
    try {
      const items = await store.listItems(id);
      if (Array.isArray(items) && items.length) return items;
    } catch (err) {
      // Nicht-fatal: es bleibt der Snapshot, sonst der Steuer-Fallback.
      console.warn('[tse] Positionen für die Betragsaufteilung nicht ladbar:', err);
    }
  }
  return resolveItems(order, opts);
}

/* --------------------------- Mock-Signatur ------------------------------- */
/**
 * Liefert eine deterministische Pseudo-Signatur. NICHT
 * KassenSichV-konform — nur für Dev und für den Adapter-Smoke-Test.
 * Format orientiert sich am BSI TR-03153 QR-Code-Schema, damit das
 * Bon-Rendering schon mal echte Daten zeigt.
 */
async function signMock(order, config, opts) {
  const timeStart = new Date().toISOString();
  // BSI process_type unterscheidet Beleg und Stornobeleg. Bei Korrektur
  // sind alle Cent-Werte bereits negativ — die process_data-Zeile reflektiert
  // das automatisch, wir markieren den process_type zusätzlich.
  const isCorrection = !!order.isCorrection;
  const processType = isCorrection ? 'Kassenbeleg-V1-Storno' : 'Kassenbeleg-V1';
  // BSI process_data (TR-03153):
  //   Beleg^<brutto 19%>_<brutto 7%>_<brutto 10,7%>_<brutto 5,5%>_<brutto 0%>
  //        ^<Zahlbetrag>:<Bar|Unbar>
  //
  // S241 / HF-10: Die Mock-Zeile stand vorher auf `Beleg^<gesamt>_<steuer19>_
  // <steuer7>_…` — falscher Slot-Belegung UND Steuer- statt Brutto-Beträge.
  // Sie ist zwar nicht rechtsverbindlich, aber sie ist die Vorlage, an der der
  // Bon-Renderer und jeder Dev-Test das Format ablesen; ein falsches Muster
  // hier hat gute Chancen, irgendwann als „so sieht das aus" zu enden.
  const split    = resolveVatSplit(order, opts);
  const totalEur = (Number(order.totalGrossCents || 0) / 100).toFixed(2);
  const payLabel = order.paymentMethod === 'card' ? 'Unbar' : 'Bar';
  const processData = buildProcessData(split, {
    totalGrossCents: Number(order.totalGrossCents || 0),
    paymentLabel: payLabel
  });
  // Mock-Counter: aktueller audit_count + 1 (monoton im Adapter-Cycle)
  const counter = Number(config.audit_count || 0) + 1;
  const txNumber = (isCorrection ? 'mock_tx_storno_' : 'mock_tx_') + order.id;
  const sigInput = [
    'mock-tss', txNumber, counter, timeStart, processType, processData
  ].join('|');
  const hash = await sha256Hex(sigInput);
  const sigBase64 = hexToBase64(hash);
  const timeEnd = new Date().toISOString();
  const qrPayload = [
    'V1', 'mock-tss', 'mock-client', txNumber, counter,
    timeStart, timeEnd, processType, processData,
    'SHA-256', sigBase64, 'mock-pubkey'
  ].join(';');

  return {
    mode: 'mock',
    txNumber,
    signatureCounter: counter,
    signatureValue: sigBase64,
    qrPayload,
    timeStart,
    timeEnd,
    request: {
      order_id: order.id,
      total_gross_cents: Number(order.totalGrossCents || 0),
      tax_7_cents:  Number(order.tax7Cents  || 0),
      tax_19_cents: Number(order.tax19Cents || 0),
      // S241 — Brutto je Satz auch im Mock-Audit, damit der Datenfluss
      // identisch zum fiskaly-Pfad ist.
      gross_7_cents:  split.gross7Cents,
      gross_19_cents: split.gross19Cents,
      gross_split_source: split.source,
      payment_method: order.paymentMethod,
      is_correction: isCorrection,
      corrects_order_id: order.correctsOrderId || null,
      mock: true
    },
    response: {
      mode: 'mock',
      hash_hex: hash,
      process_type: processType,
      process_data: processData,
      generated_at: timeEnd
    }
  };
}

/* --------------------------- Deterministische tx_id (S54) ----------------- */
/**
 * Berechnet eine deterministische fiskaly-tx_id im UUID-v4-Format aus den
 * Order-Eckdaten. Wird sowohl im User-Pfad (tse-sign Edge-Function) als
 * auch im Service-Pfad (Stripe-Webhook, Reconciliation) verwendet.
 *
 * S54-Pattern (Memory `project_gg_fiskaly_setup` Falle 6):
 *   - Vorher uuidV4() in der Edge-Function → bei Retry zweite fiskaly-TX
 *   - Jetzt Hash(restaurant_id|order_id|invoice_number|corrects_order_id)
 *     → bei Retry mit gleichem Input wird die GLEICHE tx_id berechnet,
 *     fiskaly antwortet idempotent mit der bestehenden TX statt einer
 *     neuen zu erzeugen.
 *
 * Hash → 32 Hex-Zeichen → UUID-Format `xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx`
 * (v4-konform, damit fiskaly's Pattern-Validierung greift).
 */
export async function computeDeterministicTxId(opts) {
  const parts = [
    String(opts.restaurantId || ''),
    String(opts.orderId || ''),
    String(opts.invoiceNumber || ''),
    String(opts.correctsOrderId || '')
  ].join('|');
  const hash = await sha256Hex(parts);
  // hash = 64 Hex-Zeichen (256 Bit). Wir nehmen die ersten 32 und
  // formatieren als UUID v4 (nibble 13 = 4, nibble 17 ∈ {8,9,a,b}).
  const h = hash.slice(0, 32);
  const nibble17 = ((parseInt(h[16], 16) & 0x3) | 0x8).toString(16);
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-4${h.slice(13, 16)}-${nibble17}${h.slice(17, 20)}-${h.slice(20, 32)}`;
}

/* --------------------------- fiskaly-Signatur ---------------------------- */
/**
 * Ruft die Edge Function `tse-sign` (verify_jwt=true). Die Function
 * übernimmt: pending-Row öffnen, fiskaly-Auth-Token holen, Transaktion
 * CREATE→FINISH mit deterministischer tx_id, pending-Row schließen.
 *
 * S54: Caller berechnet tx_id deterministisch, damit Retry idempotent ist.
 */
async function signFiskaly(order, config, opts) {
  if (!config.tse_fiskaly_tss_id || !config.tse_fiskaly_client_id) {
    throw new Error(
      'fiskaly nicht provisioniert. Bitte im Settings-Tab «TSE & Compliance» '
      + 'den Button «TSS + Client anlegen» klicken.'
    );
  }
  // restaurant_id wird in der Edge-Function aus auth.uid() abgeleitet —
  // für die deterministische tx_id reicht uns hier order.restaurantId,
  // falls vorhanden; sonst order.id allein (eine Order kann eh nur zu
  // einem Tenant gehören).
  const txId = await computeDeterministicTxId({
    restaurantId:    order.restaurantId || order.restaurant_id || '',
    orderId:         order.id,
    invoiceNumber:   order.invoiceNumber || order.invoice_number || '',
    correctsOrderId: order.correctsOrderId || order.corrects_order_id || ''
  });
  // S241 / HF-10 — Brutto je Steuersatz mitschicken. Wirft, wenn die Summe
  // nicht dem Zahlbetrag entspricht: dann lieber kein Beleg als ein Beleg mit
  // falsch signierten Prozessdaten.
  const split = resolveVatSplit(order, opts);
  const payload = {
    tx_id: txId,                                                     // S54: deterministisch
    order_id: order.id,
    invoice_number: order.invoiceNumber || null,
    total_gross_cents: Number(order.totalGrossCents || 0),
    tax_7_cents:  Number(order.tax7Cents  || 0),
    tax_19_cents: Number(order.tax19Cents || 0),
    // Brutto je Satz (Vorzeichen wie total_gross_cents; Storno negativ).
    gross_7_cents:  split.gross7Cents,
    gross_19_cents: split.gross19Cents,
    gross_0_cents:  split.gross0Cents,
    gross_split_source: split.source,
    subtotal_net_cents: Number(order.subtotalNetCents || 0),
    payment_method: order.paymentMethod,
    // Phase 10a.2: Stornobeleg-Flag + Verweis auf Original. Die Edge Function
    // tse-sign kann das nutzen, um den richtigen fiskaly-process_type zu setzen.
    is_correction: !!order.isCorrection,
    corrects_order_id: order.correctsOrderId || null,
    tss_id: config.tse_fiskaly_tss_id,
    client_id: config.tse_fiskaly_client_id,
    environment: config.tse_fiskaly_environment || 'TEST'
  };

  const { data, error } = await safeInvoke('tse-sign', payload);
  if (error) {
    console.error('[tse] tse-sign edge function:', error);
    throw new Error('TSE-Signatur fehlgeschlagen: ' + (error.message || error));
  }
  if (!data || !data.success) {
    const msg = (data && data.error) || 'Unbekannter Fehler bei tse-sign.';
    throw new Error(msg);
  }
  return {
    mode: 'fiskaly',
    txNumber:         data.tx_number,
    signatureCounter: Number(data.signature_counter || 0),
    signatureValue:   data.signature_value,
    qrPayload:        data.qr_payload,
    timeStart:        data.time_start,
    timeEnd:          data.time_end,
    request:          payload,
    response:         data
  };
}

/* --------------------------- Public API ---------------------------------- */

/**
 * Signiert die Bestellung gemäß aktuell konfiguriertem Modus.
 * Im 'disabled'-Modus liefert das null (kein TSE-Fluss).
 */
export async function signTransaction(order, opts) {
  if (!order || !order.id) throw new Error('order.id fehlt für TSE-Signatur.');
  const config = await getTseConfig();
  const mode = (config && config.tse_mode) || 'mock';
  if (mode === 'disabled') return null;

  // S241 / HF-10 — Positionen VOR dem Signieren auflösen: sie bestimmen das
  // Brutto je Steuersatz, das in die signierten Prozessdaten wandert.
  const items = await resolveItemsForSigning(order, opts);
  const signOpts = { ...(opts || {}), items };

  switch (mode) {
    case 'mock':     return signMock(order, config, signOpts);
    case 'fiskaly':  return signFiskaly(order, config, signOpts);
    default:
      throw new Error('Unbekannter tse_mode: ' + mode);
  }
}

/**
 * Komplettpfad: Signatur erzeugen + in DB schreiben (orders + audit).
 * Liefert { sig, order } oder null (bei disabled).
 *
 * Idempotent: Wenn die Order bereits eine tse_transaction_id hat, läuft
 * die RPC durch ohne neue Audit-Zeile (alte Werte werden zurückgegeben).
 */
export async function signAndRecord(order, opts) {
  // Idempotenz lokal: wenn schon signiert, gar nicht erst signieren
  if (order && order.tseTransactionId) {
    return { sig: null, order, alreadySigned: true };
  }
  const sig = await signTransaction(order, opts);
  if (!sig) return null; // disabled

  const supa = getSupa();
  const { data, error } = await supa.rpc('record_tse_signature', {
    p_order_id:          order.id,
    p_tx_number:         sig.txNumber,
    p_signature_counter: sig.signatureCounter,
    p_signature_value:   sig.signatureValue,
    p_qr_payload:        sig.qrPayload,
    p_time_start:        sig.timeStart,
    p_time_end:          sig.timeEnd,
    p_mode:              sig.mode,
    p_request:           sig.request,
    p_response:          sig.response
  });
  if (error) {
    console.error('[tse] record_tse_signature:', error);
    // Fehlversuch wenigstens audit_log-mäßig festhalten
    try {
      await supa.rpc('record_tse_audit', {
        p_action: 'error', p_mode: sig.mode,
        p_request: sig.request, p_response: { rpc_error: String(error.message || error) },
        p_success: false, p_error_message: String(error.message || error)
      });
    } catch (_) { /* swallow */ }
    throw error;
  }
  // Order-Daten kommen als raw-Row zurück — Caller mappt sie via storage
  // oder erhält das Update gleich via Realtime; hier nur das sig-Objekt.
  clearTseConfigCache(); // audit_count erhöht — beim nächsten Mock-Signieren frisch
  return { sig, orderRow: data };
}

/* --------------------------- ORPHAN-NACHSIGNATUR (S260 §2) --------------- */
/**
 * Einen endgültig gescheiterten Signatur-Vorgang (`tse_pending_tx.state =
 * 'orphan_failed'`) noch einmal versuchen — aus dem gespeicherten `sign_input`.
 *
 * Warum nicht über `tse-reconcile`: der Job scannt ausschliesslich
 * `state = 'pending'` und älter als 90 s. Ein Orphan fällt dort durch das
 * Raster — der Aufruf lieferte `scanned: 0`, und der Toast las sich trotzdem
 * wie Erfolg (B1-S259). Der Kommentar in der Function nennt den richtigen Weg
 * selbst: „Manuelles Retry über tse-sign nötig (idempotent)."
 *
 * Idempotent über die tx_id: dieselbe Kennung liefert bei fiskaly dieselbe
 * Transaktion, es entsteht also weder eine zweite Nummer noch ein zweiter Bon.
 * `tse-sign` schreibt die Signatur über `tse_pending_close_success` selbst auf
 * Order, Teilbeleg oder Storno-Beleg — hier ist danach nichts nachzutragen.
 *
 * TSS- und Client-Kennung kommen aus der AKTUELLEN Konfiguration, nicht aus
 * dem alten `sign_input`: zwei der vier Live-Orphans sind an `E_TSS_DELETED`
 * gescheitert. Mit der alten, gelöschten TSS zu wiederholen hiesse, denselben
 * Fehler noch einmal zu holen.
 *
 * @param {string} txId       Kennung der Pending-Zeile
 * @param {object} signInput  die gespeicherte `sign_input`-Zeile (snake_case)
 * @returns {Promise<object>} die Antwort von `tse-sign`
 */
export async function retryOrphanSignature(txId, signInput) {
  if (!txId) throw new Error('tx_id fehlt.');
  const si = signInput || {};
  if (!si.order_id) {
    throw new Error('Der gespeicherte Signatur-Auftrag hat keine Bestell-Kennung — '
                  + 'nichts, womit sich wiederholen liesse.');
  }
  const config = await getTseConfig();
  if ((config && config.tse_mode) !== 'fiskaly') {
    throw new Error('TSE-Modus ist nicht «fiskaly» — hier gibt es nichts nachzusignieren.');
  }
  if (!config.tse_fiskaly_tss_id || !config.tse_fiskaly_client_id) {
    throw new Error('fiskaly ist nicht provisioniert (TSS-/Client-Kennung fehlt).');
  }

  const num = (v) => (v == null ? null : Number(v));
  const payload = {
    tx_id:              txId,
    order_id:           si.order_id,
    invoice_number:     si.invoice_number || null,
    total_gross_cents:  Number(si.total_gross_cents || 0),
    tax_7_cents:        Number(si.tax_7_cents  || 0),
    tax_19_cents:       Number(si.tax_19_cents || 0),
    gross_7_cents:      num(si.gross_7_cents),
    gross_19_cents:     num(si.gross_19_cents),
    gross_0_cents:      num(si.gross_0_cents),
    gross_split_source: si.gross_split_source || null,
    subtotal_net_cents: Number(si.subtotal_net_cents || 0),
    payment_method:     si.payment_method || null,
    is_correction:      !!si.is_correction,
    corrects_order_id:  si.corrects_order_id || null,
    receipt_id:         si.receipt_id || null,
    correction_batch_id: si.correction_batch_id || null,
    tss_id:             config.tse_fiskaly_tss_id,
    client_id:          config.tse_fiskaly_client_id,
    environment:        config.tse_fiskaly_environment || 'TEST'
  };
  // Fehlt die Aufteilung je Satz (Pending aus der Zeit vor S241), die Felder
  // ganz weglassen statt Nullen zu schicken: `tse-sign` prüft die Invariante
  // Σ Sätze == Zahlbetrag nur, wenn welche mitkommen — mit Nullen schlüge sie
  // fehl, obwohl gar nichts behauptet wurde.
  if (payload.gross_7_cents == null && payload.gross_19_cents == null && payload.gross_0_cents == null) {
    delete payload.gross_7_cents; delete payload.gross_19_cents; delete payload.gross_0_cents;
  }

  const { data, error } = await safeInvoke('tse-sign', payload);
  if (error) {
    console.error('[tse] Nachsignatur (orphan):', error);
    throw new Error('Nachsignatur fehlgeschlagen: ' + (error.message || error));
  }
  if (!data || !data.success) {
    throw new Error((data && data.error) || 'Unbekannter Fehler bei der Nachsignatur.');
  }
  clearTseConfigCache();
  return data;
}

/* --------------------------- TEILBELEGE (S135 P4 Cut 2a) ----------------- */
/**
 * Ein Teilbeleg (order_receipts) ist nach KassenSichV ein EIGENER Kassenbeleg →
 * eigene Signatur. Der TSE-Pfad wird gespiegelt von der Order-Signatur: dieselbe
 * tse-sign-Edge-Function (sie schreibt nicht in orders, ist zieltabellen-neutral),
 * dieselbe Mock-Logik — nur die Beträge kommen vom Beleg, die tx_id leitet sich
 * aus der receipt_id ab (eigene TX je Teilbeleg) und das Record-Ziel ist
 * record_receipt_tse_signature (phase_102). Der Voll-Zahlpfad (signAndRecord)
 * bleibt unverändert.
 *
 * Der Beleg wird als „Pseudo-Order" mit den von signMock/computeDeterministicTxId
 * erwarteten Feldern verpackt — id = receipt.id (→ tx_id receipt-eindeutig).
 */
function _receiptAsOrder(r) {
  return {
    id:               r.id,
    restaurantId:     r.restaurantId || r.restaurant_id || '',
    invoiceNumber:    r.invoiceNumber || r.invoice_number || '',
    totalGrossCents:  Number(r.totalGrossCents  != null ? r.totalGrossCents  : r.total_gross_cents)  || 0,
    tax7Cents:        Number(r.tax7AmountCents  != null ? r.tax7AmountCents  : r.tax_7_amount_cents)  || 0,
    tax19Cents:       Number(r.tax19AmountCents != null ? r.tax19AmountCents : r.tax_19_amount_cents) || 0,
    subtotalNetCents: Number(r.subtotalNetCents != null ? r.subtotalNetCents : r.subtotal_net_cents)  || 0,
    // S258 — die GESPEICHERTE Aufteilung je Satz mitnehmen. `prepare_guest_receipt`
    // rechnet sie aus den Positionen DIESES Belegs, und der Stripe-Webhook
    // signiert seit S246 genau diese Werte („hier wird nichts zurueckgerechnet").
    // Ohne sie hier fiel `grossPerRateFromOrder` auf den Steuer-Rueckweg zurueck,
    // sobald der Aufrufer keine Positionen mitgibt — und genau so ein Aufrufer ist
    // das „Signatur nachholen" im Compliance-Panel, das nur den Beleg kennt.
    // `storedSplit` nimmt die Werte nur, wenn ihre Summe den Zahlbetrag trifft
    // (S241/HF-10-Invariante) — eine kaputte Zeile reicht nichts durch.
    gross7Cents:      (r.gross7Cents  != null ? r.gross7Cents  : r.gross_7_cents),
    gross19Cents:     (r.gross19Cents != null ? r.gross19Cents : r.gross_19_cents),
    gross0Cents:      (r.gross0Cents  != null ? r.gross0Cents  : r.gross_0_cents),
    paymentMethod:    r.paymentMethod || r.payment_method,
    // S241 — Rabatt-Prozentsatz durchreichen: bei einem rabattierten Teilbeleg
    // tragen die Positionen das VOR-Rabatt-Brutto, `total_gross_cents` das
    // NACH-Rabatt-Brutto (pay_order_split/phase_133). Ohne den Prozentsatz
    // könnte die Aufteilung das nicht rekonstruieren.
    discountPercent:  (r.discountPercent != null ? r.discountPercent : r.discount_percent) ?? null,
    isCorrection:     false,
    correctsOrderId:  null
  };
}

/** fiskaly-Signatur für einen Teilbeleg. Wie signFiskaly, aber: order_id =
 *  PARENT-Order (für tse_pending/Anzeige), zusätzlich receipt_id im Payload
 *  (landet im sign_input → Orphan-Recovery kann den Teilbeleg erkennen),
 *  tx_id deterministisch aus receipt.id. */
async function signReceiptFiskaly(receipt, config, opts) {
  if (!config.tse_fiskaly_tss_id || !config.tse_fiskaly_client_id) {
    throw new Error(
      'fiskaly nicht provisioniert. Bitte im Settings-Tab «TSE & Compliance» '
      + 'den Button «TSS + Client anlegen» klicken.'
    );
  }
  const r = _receiptAsOrder(receipt);
  const parentOrderId = receipt.orderId || receipt.order_id || null;
  const txId = await computeDeterministicTxId({
    restaurantId:    r.restaurantId,
    orderId:         receipt.id,                 // receipt-eindeutige tx
    invoiceNumber:   r.invoiceNumber,
    correctsOrderId: ''
  });
  // S241 / HF-10 — auch der Teilbeleg signiert Brutto je Satz. Die Positionen
  // kommen vom Aufrufer (orders-split.js kennt genau die Zeilen dieses Belegs);
  // ohne sie greift der Steuer-Fallback.
  const split = resolveVatSplit(r, opts);
  const payload = {
    tx_id: txId,
    order_id: parentOrderId,                     // Eltern-Order (tse_pending/Anzeige)
    receipt_id: receipt.id,                       // S135 P4: Teilbeleg-Kennung im sign_input
    invoice_number: r.invoiceNumber || null,
    total_gross_cents: r.totalGrossCents,
    tax_7_cents:  r.tax7Cents,
    tax_19_cents: r.tax19Cents,
    gross_7_cents:  split.gross7Cents,
    gross_19_cents: split.gross19Cents,
    gross_0_cents:  split.gross0Cents,
    gross_split_source: split.source,
    subtotal_net_cents: r.subtotalNetCents,
    payment_method: r.paymentMethod,
    is_correction: false,
    corrects_order_id: null,
    tss_id: config.tse_fiskaly_tss_id,
    client_id: config.tse_fiskaly_client_id,
    environment: config.tse_fiskaly_environment || 'TEST'
  };

  const { data, error } = await safeInvoke('tse-sign', payload);
  if (error) {
    console.error('[tse] tse-sign edge function (receipt):', error);
    throw new Error('TSE-Signatur (Teilbeleg) fehlgeschlagen: ' + (error.message || error));
  }
  if (!data || !data.success) {
    const msg = (data && data.error) || 'Unbekannter Fehler bei tse-sign (Teilbeleg).';
    throw new Error(msg);
  }
  return {
    mode: 'fiskaly',
    txNumber:         data.tx_number,
    signatureCounter: Number(data.signature_counter || 0),
    signatureValue:   data.signature_value,
    qrPayload:        data.qr_payload,
    timeStart:        data.time_start,
    timeEnd:          data.time_end,
    request:          payload,
    response:         data
  };
}

/** Signiert einen Teilbeleg gemäß Modus. disabled → null. mock reuse signMock. */
export async function signReceiptTransaction(receipt, opts) {
  if (!receipt || !receipt.id) throw new Error('receipt.id fehlt für TSE-Signatur.');
  const config = await getTseConfig();
  const mode = (config && config.tse_mode) || 'mock';
  // S241 — Positionen NUR aus dem Aufrufer (orders-split.js kennt die Zeilen
  // dieses Teilbelegs). Ein DB-Nachladen wie im Order-Pfad wäre hier falsch:
  // `listItems` schlägt über die order_id nach und lieferte die Zeilen der
  // GANZEN Order, nicht die des Belegs.
  const signOpts = { ...(opts || {}), items: resolveItems(receipt, opts) };
  switch (mode) {
    case 'disabled': return null;
    case 'mock':     return signMock(_receiptAsOrder(receipt), config, signOpts);
    case 'fiskaly':  return signReceiptFiskaly(receipt, config, signOpts);
    default:
      throw new Error('Unbekannter tse_mode: ' + mode);
  }
}

/**
 * Komplettpfad für einen Teilbeleg: Signatur erzeugen + in order_receipts
 * schreiben (record_receipt_tse_signature, phase_102). Spiegel von
 * signAndRecord(order). Idempotent über receipt.tseTransactionId.
 * Liefert { sig, receiptRow } oder null (disabled) / { alreadySigned }.
 */
export async function signAndRecordReceipt(receipt, opts) {
  if (receipt && receipt.tseTransactionId) {
    return { sig: null, receipt, alreadySigned: true };
  }
  const sig = await signReceiptTransaction(receipt, opts);
  if (!sig) return null; // disabled

  const supa = getSupa();
  const { data, error } = await supa.rpc('record_receipt_tse_signature', {
    p_receipt_id:        receipt.id,
    p_tx_number:         sig.txNumber,
    p_signature_counter: sig.signatureCounter,
    p_signature_value:   sig.signatureValue,
    p_qr_payload:        sig.qrPayload,
    p_time_start:        sig.timeStart,
    p_time_end:          sig.timeEnd,
    p_mode:              sig.mode,
    p_request:           sig.request,
    p_response:          sig.response
  });
  if (error) {
    console.error('[tse] record_receipt_tse_signature:', error);
    try {
      await supa.rpc('record_tse_audit', {
        p_action: 'error', p_mode: sig.mode,
        p_request: sig.request, p_response: { rpc_error: String(error.message || error) },
        p_success: false, p_error_message: String(error.message || error)
      });
    } catch (_) { /* swallow */ }
    throw error;
  }
  clearTseConfigCache();
  return { sig, receiptRow: data };
}

/**
 * Audit-Eintrag ohne Order (z.B. fehlgeschlagene Provisioning, Mode-Wechsel).
 * Dünner Wrapper über record_tse_audit-RPC.
 */
export async function logTseAudit({ action, mode, request, response, success = true, errorMessage = null }) {
  const supa = getSupa();
  const { data, error } = await supa.rpc('record_tse_audit', {
    p_action:        action,
    p_mode:          mode,
    p_request:       request || null,
    p_response:      response || null,
    p_success:       !!success,
    p_error_message: errorMessage
  });
  if (error) throw error;
  return data;
}

/* --------------------------- KORREKTUR-BELEGE (S242 · HF-11) -------------- */
/**
 * Ein Teil-Storno ist ein EIGENER Kassenbeleg — eigene Belegnummer, eigene
 * TSE-Transaktion, umsatzmindernd. Bis S241 wurde er NIE signiert:
 *
 *   `submitPartialCorrectionDialog` baute `{...order, id:'corr_…'}`. Der Spread
 *   erbte `tseTransactionId` der bezahlten Mutter-Order, `signAndRecord` kehrte
 *   sofort mit `alreadySigned: true` zurück — ohne Fehler, ohne Toast, ohne
 *   Edge-Call. Alle 23 order_corrections in der Live-DB hatten
 *   `tse_transaction_id IS NULL`.
 *
 * Selbst ohne diesen Guard wäre der Persist gescheitert: `record_tse_signature`
 * schlägt über die ORDER-ID nach, und eine `corr_…`-ID existiert dort nicht.
 * Deshalb hat der Batch — wie der Teilbeleg seit phase_106 — einen eigenen
 * Record-Pfad (`record_correction_tse_signature_service`, phase_173).
 *
 * Die Pseudo-Order wird explizit OHNE TSE-Felder gebaut; sie darf nichts von
 * der Mutter-Order erben.
 */
function _batchAsOrder(batch) {
  const b = batch || {};
  return {
    id:               b.id,
    restaurantId:     b.restaurantId || b.restaurant_id || '',
    orderNumber:      b.invoiceNumber || b.invoice_number || b.id,
    invoiceNumber:    b.invoiceNumber || b.invoice_number || '',
    // Beträge sind im Batch bereits NEGATIV gespeichert (umsatzmindernd) —
    // grossPerRateFromOrder spiegelt das Vorzeichen auf die Positionen.
    totalGrossCents:  Number(b.totalGrossCents  != null ? b.totalGrossCents  : b.total_gross_cents)  || 0,
    tax7Cents:        Number(b.tax7Cents        != null ? b.tax7Cents        : b.tax_7_cents)        || 0,
    tax19Cents:       Number(b.tax19Cents       != null ? b.tax19Cents       : b.tax_19_cents)       || 0,
    subtotalNetCents: Number(b.subtotalNetCents != null ? b.subtotalNetCents : b.subtotal_net_cents) || 0,
    gross7Cents:      (b.gross7Cents  != null) ? b.gross7Cents  : (b.gross_7_cents  != null ? b.gross_7_cents  : null),
    gross19Cents:     (b.gross19Cents != null) ? b.gross19Cents : (b.gross_19_cents != null ? b.gross_19_cents : null),
    gross0Cents:      (b.gross0Cents  != null) ? b.gross0Cents  : (b.gross_0_cents  != null ? b.gross_0_cents  : null),
    paymentMethod:    b.paymentMethod || b.payment_method || 'cash',
    isCorrection:     true,
    correctsOrderId:  b.orderId || b.order_id || null,
    // KEINE tse*-Felder — genau die haben den Storno bisher stumm geschaltet.
    tseTransactionId: null
  };
}

/** fiskaly-Signatur für einen Korrektur-Batch. Wie signReceiptFiskaly, aber:
 *  order_id = Mutter-Order (tse_pending/Audit), correction_batch_id im Payload,
 *  tx_id deterministisch aus batch.id, is_correction = true. */
async function signCorrectionFiskaly(batch, config, opts) {
  if (!config.tse_fiskaly_tss_id || !config.tse_fiskaly_client_id) {
    throw new Error(
      'fiskaly nicht provisioniert. Bitte im Settings-Tab «TSE & Compliance» '
      + 'den Button «TSS + Client anlegen» klicken.'
    );
  }
  const b = _batchAsOrder(batch);
  const parentOrderId = b.correctsOrderId;
  const txId = await computeDeterministicTxId({
    restaurantId:    b.restaurantId,
    orderId:         batch.id,                   // batch-eindeutige tx
    invoiceNumber:   b.invoiceNumber,
    correctsOrderId: parentOrderId || ''
  });
  const split = resolveVatSplit(b, opts);
  const payload = {
    tx_id: txId,
    order_id: parentOrderId,                     // Mutter-Order (tse_pending/Audit)
    correction_batch_id: batch.id,               // phase_173: dritter Zweig
    invoice_number: b.invoiceNumber || null,
    total_gross_cents: b.totalGrossCents,
    tax_7_cents:  b.tax7Cents,
    tax_19_cents: b.tax19Cents,
    gross_7_cents:  split.gross7Cents,
    gross_19_cents: split.gross19Cents,
    gross_0_cents:  split.gross0Cents,
    gross_split_source: split.source,
    subtotal_net_cents: b.subtotalNetCents,
    payment_method: b.paymentMethod,
    is_correction: true,
    corrects_order_id: parentOrderId,
    tss_id: config.tse_fiskaly_tss_id,
    client_id: config.tse_fiskaly_client_id,
    environment: config.tse_fiskaly_environment || 'TEST'
  };

  const { data, error } = await safeInvoke('tse-sign', payload);
  if (error) {
    console.error('[tse] tse-sign edge function (correction):', error);
    throw new Error('TSE-Signatur (Teil-Storno) fehlgeschlagen: ' + (error.message || error));
  }
  if (!data || !data.success) {
    const msg = (data && data.error) || 'Unbekannter Fehler bei tse-sign (Teil-Storno).';
    throw new Error(msg);
  }
  return {
    mode: 'fiskaly',
    txNumber:         data.tx_number,
    signatureCounter: Number(data.signature_counter || 0),
    signatureValue:   data.signature_value,
    qrPayload:        data.qr_payload,
    timeStart:        data.time_start,
    timeEnd:          data.time_end,
    request:          payload,
    response:         data
  };
}

/** Signiert einen Korrektur-Batch gemäß Modus. disabled → null. */
export async function signCorrectionTransaction(batch, opts) {
  if (!batch || !batch.id) throw new Error('batch.id fehlt für TSE-Signatur.');
  if ((batch.kind || 'refund') !== 'refund') {
    // Warenverlust ist kein Beleg — er wird bewusst nicht signiert.
    return null;
  }
  const config = await getTseConfig();
  const mode = (config && config.tse_mode) || 'mock';
  // Positionen kommen vom Aufrufer (die stornierten Zeilen). Kein DB-Nachladen:
  // `listItems` würde über die Mutter-Order die GANZE Bestellung liefern.
  const signOpts = { ...(opts || {}), items: resolveItems(batch, opts), feeInfo: null };
  switch (mode) {
    case 'disabled': return null;
    case 'mock':     return signMock(_batchAsOrder(batch), config, signOpts);
    case 'fiskaly':  return signCorrectionFiskaly(batch, config, signOpts);
    default:
      throw new Error('Unbekannter tse_mode: ' + mode);
  }
}

/**
 * Komplettpfad für einen Korrektur-Batch. Der Persist passiert server-seitig in
 * `tse_pending_close_success` → `record_correction_tse_signature_service`
 * (phase_173) — der Client braucht KEINEN eigenen record-RPC mehr. Genau da lag
 * der zweite Teil des Fehlers: `set_order_correction_tse` war SECURITY DEFINER
 * ohne Auth-Prüfung und schrieb nur eine Transaktionsnummer, keine Signatur.
 *
 * Liefert { sig, batch } oder null (disabled) / { alreadySigned }.
 */
export async function signAndRecordCorrection(batch, opts) {
  if (batch && batch.tseTransactionId) {
    return { sig: null, batch, alreadySigned: true };
  }
  const sig = await signCorrectionTransaction(batch, opts);
  if (!sig) return null;

  if (sig.mode === 'mock') {
    // Im Mock-Modus gibt es keinen Edge-Call und damit keinen Server-Persist.
    // Der Audit-Eintrag hält den Vorgang trotzdem fest (wie im Order-Pfad).
    try {
      await logTseAudit({
        action: 'sign', mode: 'mock',
        request: sig.request, response: sig.response, success: true
      });
    } catch (err) {
      console.warn('[tse] Mock-Audit für Korrektur-Batch fehlgeschlagen:', err);
    }
  }
  clearTseConfigCache();
  return { sig, batch };
}

/* --------------------------- window-Backward-Compat ---------------------- */

if (typeof window !== 'undefined') {
  Object.assign(window, {
    tseSignAndRecord: signAndRecord,
    tseSignAndRecordReceipt: signAndRecordReceipt,
    tseRetryOrphanSignature: retryOrphanSignature,
    tseSignAndRecordCorrection: signAndRecordCorrection,
    tseSignTransaction: signTransaction,
    tseComputeDeterministicTxId: computeDeterministicTxId,
    getTseConfig,
    setTseMode,
    clearTseConfigCache,
    logTseAudit
  });
}
